import React, { useContext, useMemo, useCallback } from 'react';
import { ViewCategoryContext, IViewCategoryContext } from '../viewCategoryState/viewCategoryContext';
import { Link } from 'react-router-dom';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useArchivedSingleSelect } from 'hooks/useArchivedSingleSelect';
import { categoryTypes, typesOfCategoriesDeniedForClientManagers } from 'appConstants';
import { UserRoles, RequestEntity } from 'types';
import { useTranslation } from 'react-i18next';
import ImgUploaderByEntityId from 'components/ImgUploader/ImgUploaderByEntityId';
import { ViewPageBlock } from 'components/ViewPage/ViewPageBlock';
import { ViewPageSection } from 'components/ViewPage/ViewPageSection';
import { ItemList } from 'components/ViewPage//ItemList';
import { NoItemsNotification } from 'components/ViewPage/NoItemsNotification';
import Text from 'components/Text/Text';
import FullInfoField from 'components/FullInfoField/FullInfoField';
import { MenuTranslationForm } from 'components/TranslationForm/MenuTranslationForm';
import Button from 'components/Button/Button';
import OutsideFormSelect from 'components/Select/OutsideFormSelect';
import { DropdownRender } from 'components/Select/DropdownRender';
import { ShowArchived } from 'components/Select/DropdownRender/ShowArchived';
import breadcrumbs from 'images/breadcrumbs.svg';
import style from './ViewForm.module.scss';

export const ViewForm: React.FC = () => {
	const {
		setTranslationTab,
		changeTypeOfModalWindow,
		setRestaurantIdForRestaurantProps,
		categoryFullInfo,
		accessibleRestaurants,
		selectedTranslationTab,
		restaurantIdForRestaurantProps,
		restaurantProps,
	} = useContext<IViewCategoryContext>(ViewCategoryContext);
	const isDeniedForManagers =
		!!categoryFullInfo?.type && typesOfCategoriesDeniedForClientManagers.includes(categoryFullInfo.type);
	const hasEditAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		isDeniedForManagers ? UserRoles.NON_EXISTENT_ROLE : UserRoles.CLIENT_MANAGER,
		isDeniedForManagers ? UserRoles.NON_EXISTENT_ROLE : UserRoles.GROUP_MANAGER,
		UserRoles.RESTAURANT_MANAGER,
		UserRoles.TECHNICAL,
		UserRoles.WAITER,
		UserRoles.RKL_SUPPORT_MANAGER,
	]);
	const { t } = useTranslation('form');
	const isRestricted = categoryFullInfo?.isRestricted;
	const restrictedValue = isRestricted ? t('yes') : t('no');
	const hasSchedulePriority = categoryFullInfo?.overrideScheduleAvailability;
	const schedulePriorityValue = hasSchedulePriority ? t('yes') : t('no');
	const categoryTypeName = categoryTypes.find((el) => el.categoryID === categoryFullInfo?.type)?.categoryName || null;
	const listOfRestaurants = useMemo(() => accessibleRestaurants, [accessibleRestaurants]);
	const {
		showArchived,
		toggleShowArchived,
		options: mappedRestaurantOptions,
	} = useArchivedSingleSelect({
		items: accessibleRestaurants,
		entityType: 'restaurants',
		keepVisibleIds: restaurantIdForRestaurantProps ? [restaurantIdForRestaurantProps] : [],
	});

	const handleClickAssignRestaurant = useCallback(
		() => changeTypeOfModalWindow('assignRestaurants'),
		[changeTypeOfModalWindow],
	);

	const renderSelectMenu = useCallback(
		(menu: React.ReactElement) => (
			<DropdownRender menu={menu}>
				<ShowArchived checked={showArchived} onChange={toggleShowArchived} />
			</DropdownRender>
		),
		[showArchived, toggleShowArchived],
	);

	return (
		<div className={style.container} data-test="ViewForm">
			<div className={style.content}>
				<ViewPageBlock heading="generalPropertiesHeading">
					<ViewPageSection heading="mainInfoHeading">
						<div className={style.row}>
							<div className={style.imgWrapper}>
								{categoryFullInfo?.id && categoryFullInfo.photoPath ? (
									<ImgUploaderByEntityId
										resource={RequestEntity.CategoriesMenuV2}
										entityID={categoryFullInfo.id}
										isMenuV2={true}
										photoSize="Medium"
										loaderSize="big"
										key="1"
									/>
								) : (
									<ImgUploaderByEntityId
										resource={RequestEntity.CategoriesMenuV2}
										entityID=""
										isMenuV2={true}
										photoSize="Medium"
										loaderSize="big"
										key="2"
									/>
								)}
							</div>
							<div className={style.mainInfoInputs}>
								<FullInfoField label="nameCategoryLabel" value={categoryFullInfo?.name || '-'} />
								<FullInfoField
									label="categoryTypeLabel"
									translationNameSpace="form"
									value={categoryTypeName || '-'}
								/>
								<FullInfoField label="customerLabel" value={categoryFullInfo?.customer.name || '-'} />
							</div>
						</div>
					</ViewPageSection>

					<ViewPageSection heading="restrictionHeading">
						<FullInfoField
							label="isRestrictedLabel"
							value={restrictedValue}
							prefixIcon="asCheckbox"
							prefixIconName={isRestricted ? 'checkMark' : 'crossMark'}
							prefixIconIsChecked={isRestricted}
						/>
					</ViewPageSection>

					<ViewPageSection heading="translationsHeading">
						<MenuTranslationForm
							className={style.translationForm}
							setTranslationTab={setTranslationTab}
							isShowLanguageName={false}
							IDSelectedCustomer={categoryFullInfo?.customer.id || ''}
							translations={categoryFullInfo?.translations || []}
							selectedTranslationTab={selectedTranslationTab}
							nameLabel="nameCategoryLabel"
						/>
					</ViewPageSection>

					<ViewPageSection heading="scheduleHeading">
						<FullInfoField
							label="hasSchedulePriorityLabel"
							value={schedulePriorityValue}
							prefixIcon="asCheckbox"
							prefixIconName={hasSchedulePriority ? 'checkMark' : 'crossMark'}
							prefixIconIsChecked={hasSchedulePriority}
						/>
						<div className={style.row}>
							<FullInfoField
								label="activationScheduleLabel"
								value={categoryFullInfo?.activationCron || '-'}
							/>
							<FullInfoField
								label="deactivationScheduleLabel"
								value={categoryFullInfo?.deactivationCron || '-'}
							/>
						</div>
					</ViewPageSection>

					<ViewPageSection heading="restaurantsHeading">
						{listOfRestaurants.length ? (
							<ItemList
								itemListLabel="allRestaurantsWithThisCategory"
								items={listOfRestaurants}
								pathToItem="/clients/restaurants?restaurantFullInfoID="
								assignBtnTranslationKey="editRestaurantsList"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						) : (
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerCategoryToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						)}
					</ViewPageSection>

					<div className={style.btnWrapper} data-test="BtnBlock1">
						{/* <Button theme="primaryBlue" disabled={true} onClick={() => console.log('Check tree view')}>
							<Text translationNameSpace="form" translationKey="checkTreeViewButton" asFragment={true} />
						</Button> */}
						{hasEditAccess && (
							<Link
								target={`blank_${Date.now()}`}
								rel="noopener noreferrer"
								to={`/menu-v2/category/edit?categoryID=${categoryFullInfo?.id}`}>
								<Button
									className={style.btn}
									theme="primaryBlue"
									icon={
										<svg className={style.icon}>
											<use xlinkHref={`${breadcrumbs}#pencil`} />
										</svg>
									}>
									<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
								</Button>
							</Link>
						)}
					</div>
				</ViewPageBlock>

				<ViewPageBlock heading="restaurantPropertiesHeading">
					{accessibleRestaurants.length ? (
						<>
							<OutsideFormSelect
								testId="restaurantProperties"
								label="restaurantsLabel"
								name="restaurantProperties"
								value={restaurantIdForRestaurantProps}
								options={mappedRestaurantOptions}
								onChange={setRestaurantIdForRestaurantProps}
								keepSearchWithDropdownRender={true}
								dropdownRender={renderSelectMenu}
							/>

							<ViewPageSection heading="subcategoryHeading">
								<ItemList
									items={restaurantProps?.subCategories || []}
									pathToItem="/menu-v2/subcategory/edit?subcategoryID="
								/>
							</ViewPageSection>

							<ViewPageSection heading="scheduleHeading">
								<div className={style.row}>
									<FullInfoField
										containerClassName={style.marginTopZero}
										label="activationScheduleLabel"
										value={restaurantProps?.activationCron || '-'}
									/>
									<FullInfoField
										containerClassName={style.marginTopZero}
										label="deactivationScheduleLabel"
										value={restaurantProps?.deactivationCron || '-'}
									/>
								</div>
							</ViewPageSection>
						</>
					) : (
						<ViewPageSection heading="restaurantsHeading">
							<NoItemsNotification
								title="noRestaurantsAssigned"
								description="assignRestaurantsInTheCustomerCategoryToBeAbleToEditThem"
								assignBtnTranslationKey="assignRestaurantsButton"
								onClickShowAssignModal={handleClickAssignRestaurant}
							/>
						</ViewPageSection>
					)}

					{hasEditAccess && restaurantIdForRestaurantProps && (
						<div className={style.btnWrapper} data-test="BtnBlock2">
							<Link
								target={`blank_${Date.now()}`}
								rel="noopener noreferrer"
								to={`/menu-v2/category/edit-restaurant-properties?categoryID=${
									categoryFullInfo?.id || ''
								}`}>
								<Button
									className={style.btn}
									theme="primaryBlue"
									icon={
										<svg className={style.icon}>
											<use xlinkHref={`${breadcrumbs}#pencil`} />
										</svg>
									}>
									<Text translationNameSpace="form" translationKey="editButton" asFragment={true} />
								</Button>
							</Link>
						</div>
					)}
				</ViewPageBlock>
			</div>
		</div>
	);
};
